import { rackCapacity } from '../data/coinUpgrades';
// Draining table, brine tub, and cellar panels.
import { CELLAR } from '../config/constants';
import { CHEESES, agingStageLabel } from '../data/cheeses';
import * as Cellar from '../core/Cellar';
import type { RenderResult } from './Panel';
import { $, fmt, renderStage, starText } from './panels';
import type { UI } from './UI';

export function openTablePanel(ui: UI): void {
  const game = ui.game;
  ui.panels.open({
    id: 'table',
    title: 'Draining table',
    kind: 'task',
    side: ui.side(),
    render(body, api): RenderResult {
      const b = game.state.table.batch;
      if (!b) {
        const block = game.recipeBlock('taleggio');
        body.innerHTML = `<div class="muted">Nothing on the table. Taleggio curd comes here from the vat to be cut, molded, and drained.</div>${block ? `<div class="muted">${block}</div>` : ''}`;
        return {};
      }
      return renderStage(ui, body, b, 'table', api.rerender);
    },
  });
}

export function openTubPanel(ui: UI): void {
  const game = ui.game;
  ui.panels.open({
    id: 'tub',
    title: 'Brine tub',
    kind: 'task',
    side: ui.side(),
    render(body, api): RenderResult {
      const b = game.state.tub.batch;
      if (!b) {
        body.innerHTML = `<div class="muted">The brine tub is empty. Drained taleggio squares come here to be salted before the cellar.</div>`;
        return {};
      }
      return renderStage(ui, body, b, 'tub', api.rerender);
    },
  });
}

export function openCellarPanel(ui: UI): void {
  const game = ui.game;
  ui.panels.open({
    id: 'cellar',
    title: 'Aging cellar',
    kind: 'menu',
    side: ui.side(),
    render(body): RenderResult {
      body.innerHTML = `
        <div class="stat"><span>Conditions</span><b class="env"></b></div>
        <div class="dial"><span class="muted">Temp</span><input type="range" class="temp" min="35" max="75" step="0.5"><b class="tempv"></b></div>
        <div class="dial"><span class="muted">Humidity</span><input type="range" class="hum" min="40" max="100" step="0.5"><b class="humv"></b></div>
        <div class="muted">Target ${CELLAR.TEMP_LOW} to ${CELLAR.TEMP_HIGH}°F and ${CELLAR.HUM_LOW} to ${CELLAR.HUM_HIGH}%. Both in range ages ×${CELLAR.SPEED_OPTIMAL} a day, one in range ×${CELLAR.SPEED_HALF}, neither stalls (${CELLAR.STALL_SPOIL_DAYS} stalled days spoils the racks). The room drifts every night.</div>
        <div class="section row"><button class="btn primary wash">Wash rinds (1 salt)</button><span class="muted washinfo"></span></div>
        <div class="section pending"></div>
        <div class="section"><h4>Racks <span class="muted cap"></span></h4><div class="racks"></div></div>`;
      const env = $(body, '.env');
      const temp = $<HTMLInputElement>(body, '.temp');
      const hum = $<HTMLInputElement>(body, '.hum');
      const tempv = $(body, '.tempv');
      const humv = $(body, '.humv');
      const wash = $<HTMLButtonElement>(body, '.wash');
      const washinfo = $(body, '.washinfo');
      const pending = $(body, '.pending');
      const cap = $(body, '.cap');
      const racks = $(body, '.racks');
      temp.oninput = () => game.setCellarDials(Number(temp.value), game.state.cellar.humidity);
      hum.oninput = () => game.setCellarDials(game.state.cellar.temp, Number(hum.value));
      wash.onclick = () => game.washRinds();
      let sig = '';
      const update = (): void => {
        const s = game.state;
        const c = s.cellar;
        if (document.activeElement !== temp) temp.value = String(c.temp);
        if (document.activeElement !== hum) hum.value = String(c.humidity);
        tempv.textContent = `${fmt(c.temp, 1)}°F`;
        tempv.className = Cellar.tempOk(c.temp) ? 'good' : 'bad';
        humv.textContent = `${fmt(c.humidity, 1)}%`;
        humv.className = Cellar.humOk(c.humidity) ? 'good' : 'bad';
        env.textContent = `${Cellar.envLabel(c)} (×${Cellar.envSpeed(c)} aging speed)`;
        env.className = Cellar.envSpeed(c) === CELLAR.SPEED_OPTIMAL ? 'good' : Cellar.envSpeed(c) === 0 ? 'bad' : '';
        const due = Cellar.stacksNeedingWash(s).length;
        wash.disabled = due === 0 || s.supplies.salt <= 0;
        washinfo.textContent = due ? `${due} rack${due === 1 ? '' : 's'} due · ${s.supplies.salt} salt` : `Nothing due · ${s.supplies.salt} salt`;
        cap.textContent = `${Cellar.rackCount(s)} / ${rackCapacity(s)}`;
        const tub = s.tub.batch;
        const nextSig = c.racks.map((r) => `${r.id}:${r.qty}:${r.ageDays}:${r.missedWashes}:${r.lastWashAge}`).join('|') + `|${tub?.stage ?? ''}:${tub?.units ?? 0}|${s.supplies.packaging}`;
        if (nextSig === sig) return;
        sig = nextSig;
        pending.innerHTML = '';
        if (tub && tub.stage === 'rack') {
          pending.innerHTML = `<div class="row"><button class="btn primary rackbtn">Rack ${tub.units} square${tub.units === 1 ? '' : 's'} from the brine tub</button></div>`;
          $<HTMLButtonElement>(pending, '.rackbtn').onclick = () => game.rackSquares();
        }
        racks.innerHTML = c.racks.length ? '' : '<div class="muted">Empty racks. Brined taleggio squares go here.</div>';
        for (const stack of c.racks) {
          const def = CHEESES[stack.cheeseId];
          const rule = def.aging.ageable ? def.aging : null;
          const row = document.createElement('div');
          row.className = 'item';
          const mult = Cellar.stackValueMult(stack, s);
          const washDue = Cellar.stackNeedsWash(stack);
          row.innerHTML = `<div class="title">${stack.qty} × ${def.name} ${starText(stack.stars)}</div>
            <div class="muted">Age ${fmt(stack.ageDays, 1)} days${rule ? ` · ${agingStageLabel(rule, stack.ageDays)} · min ${rule.minAgeDays}, peak ${rule.peakDays} to ${rule.holdUntilDays}` : ''} · value ×${fmt(mult)}${stack.missedWashes ? ` · ${stack.missedWashes} missed wash${stack.missedWashes === 1 ? '' : 'es'}` : ''}${washDue ? ' · <span class="bad">wash due</span>' : ''}</div>
            <div class="row"></div>`;
          const r = $(row, '.row');
          const one = document.createElement('button');
          one.className = 'btn small';
          one.textContent = 'Wrap 1';
          one.onclick = () => game.takeOutWheels(stack.id, 1);
          const all = document.createElement('button');
          all.className = 'btn small';
          all.textContent = 'Wrap all';
          all.onclick = () => game.takeOutWheels(stack.id, stack.qty);
          r.append(one, all);
          if (rule && stack.ageDays < rule.minAgeDays) r.insertAdjacentHTML('beforeend', `<span class="muted">${Math.ceil((rule.minAgeDays - stack.ageDays) / Math.max(0.01, Cellar.envSpeed(c) || 1))} days to minimum age</span>`);
          racks.appendChild(row);
        }
      };
      update();
      return { update };
    },
  });
}
